import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './digital-signage.scss';
import Layout from '../../../../layout/Layout';
import Card from 'react-bootstrap/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faCircleInfo,
    faSpinner,
    faTrash
} from '@fortawesome/free-solid-svg-icons';
import { Modal, Button } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { handleScroll } from '../../../../helpers/signageScrollHandler';
import {
    deleteSignages,
    fetchMarketingOrderContent
} from '../../../../redux/slices/myGymTV/signageSlice.js';
import { useMediaQuery } from 'react-responsive';

import moment from 'moment';

const DigitalSignage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const memoizedHandleScroll = useCallback(handleScroll, []);
    const [contentMap, setContentMap] = useState({});
    const [nowPlayingSignages, setNowPlayingSignages] = useState([]);
    const [scheduledSignages, setScheduledSignages] = useState([]);
    const [expiredSignages, setExpiredSignages] = useState([]);
    const [mediaUrls, setMediaUrls] = useState({});
    const signages =
        useSelector((state) => state.signage.marketingOrderContent) || [];
    const isMobile = useMediaQuery({
        query: '(max-device-width: 1023px)'
    });

    const displayMedia = (contentType, url) => {
        if (!url) {
            // Show a spinner while the media is loading
            return (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <FontAwesomeIcon
                        icon={faSpinner}
                        size="2x"
                        spin
                    />
                </div>
            );
        }

        if (contentType === 'I') {
            // Render an image if the contentType is 'I'
            return (
                <img
                    alt="media"
                    src={url}
                    style={{ maxHeight: 'auto', width: '100%' }}
                />
            );
        } else if (contentType === 'V') {
            // Render an MP4 video if the contentType is 'V'
            return (
                <video
                    controls
                    style={{ maxHeight: 'auto', width: '100%' }}
                >
                    <source
                        src={url}
                        type="video/mp4"
                    />
                    Your browser does not support the video tag.
                </video>
            );
        }

        return null;
    };

    const fetchData = async (contentID) => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('Token not found');
            return;
        }
        if (contentID > 0) {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BASE_URL}/api/DigitalSignage/${contentID}/content`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                setMediaUrls((prev) => ({ ...prev, [contentID]: url }));
            } catch (error) {
                console.error('There was a problem!', error);
            }
        }
    };

    useEffect(() => {
        const fetchMarketingData = async () => {
            await dispatch(fetchMarketingOrderContent());
        };

        fetchMarketingData();
    }, [dispatch]);

    const navigateToDetails = (id) => {
        navigate(`/signage-details/${id}`);
    };

    useEffect(() => {
        // Use moment.utc to work with the dates in UTC
        const today = moment.utc().startOf('day'); // Today's date at UTC midnight
        const nowPlaying = [];
        const scheduled = [];
        const expired = [];

        for (const signage of signages) {
            const contentID = signage.ContentID;
            if (contentID) {
                fetchData(contentID);
            }
            // Parse the payload dates in UTC
            const startDate = signage.StartDate ? moment.utc(signage.StartDate) : null;
            const endDate = signage.EndDate ? moment.utc(signage.EndDate) : null;

            // Now Playing: Signage is active and today is between start and end (or no end)
            if (
                signage.ContentID > 0 &&
                signage.IsActive &&
                (!startDate || startDate.isSameOrBefore(today)) &&
                (!endDate || endDate.isAfter(today))
            ) {
                nowPlaying.push(signage);
            }
            // Scheduled: Signage is active but its start date is in the future
            else if (
                signage.ContentID > 0 &&
                signage.IsActive &&
                startDate && startDate.isAfter(today) &&
                (!endDate || endDate.isAfter(today))
            ) {
                scheduled.push(signage);
            }
            // Expired: Signage is either inactive or its end date has passed
            else if (
                signage.ContentID > 0 &&
                (!signage.IsActive || (endDate && endDate.isBefore(today)))
            ) {
                expired.push(signage);
            }
        }

        setNowPlayingSignages(nowPlaying);
        setScheduledSignages(scheduled);
        setExpiredSignages(expired);
    }, [signages]);


    const [showModal, setShowModal] = useState(false);

    const handleOpenModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };


    useEffect(() => {
        const nowPlayingHeader = document.getElementById('now-playing-header');
        if (nowPlayingHeader) {
            nowPlayingHeader.style.display = 'block';
        }

        window.addEventListener('scroll', memoizedHandleScroll);
        return () => window.removeEventListener('scroll', memoizedHandleScroll);
    }, [memoizedHandleScroll]);

    return (
        <Layout>
            <div className="digital-signage-header">
                <div className="card-header">Digital Signage</div>
                {!isMobile && (
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            navigate('/upload-signage');
                        }}
                    >
                        Add New Signage
                    </button>
                )}

                {nowPlayingSignages.length > 0 ||
                    scheduledSignages.length > 0 ||
                    expiredSignages.length > 0 ? (
                    <div>
                        <div
                            className="card-header"
                            id="now-playing-header"
                        >
                            Now Playing ({nowPlayingSignages.length})
                        </div>
                        <div
                            className="card-header"
                            id="scheduled-header"
                        >
                            Coming Soon ({scheduledSignages.length})
                        </div>
                        <div
                            className="card-header"
                            id="expired-header"
                        >
                            Expired ({expiredSignages.length})
                            <FontAwesomeIcon
                                icon={faTrash}
                                onClick={handleOpenModal}
                                style={{ cursor: 'pointer', marginLeft: '10px' }}
                            />
                        </div>
                    </div>
                ) : null}
            </div>

            {nowPlayingSignages.length > 0 ||
                scheduledSignages.length > 0 ||
                expiredSignages.length > 0 ? (
                <div>
                    <Card className="digital-signage container border-0">
                        <div
                            className="signage-section"
                            id="now-playing-section"
                        >
                            <Card.Header
                                className="text-end"
                                id="coming-soon-gallery-header"
                            >{`Now Playing (${nowPlayingSignages.length})`}</Card.Header>
                            <Card.Body>
                                <div className="row">
                                    {nowPlayingSignages.map((signage) => (
                                        <div
                                            className="col-lg-3 col-md-4 col-sm-6 col-12 text-center mx-2 m-2 py-2 border rounded-1"
                                            key={signage.ContentID}
                                        >
                                            <div className="now-playing-tag d-flex justify-content-between align-items-center w-100">
                                                {signage.EndDate &&
                                                    moment(signage.EndDate).year() !== 9999 ? (
                                                    <span>
                                                        Now playing through{' '}
                                                        {moment(signage.EndDate).format('MM/DD/YYYY')}
                                                    </span>
                                                ) : (
                                                    <span>Now playing with no end date</span>
                                                )}
                                                <FontAwesomeIcon
                                                    className="ms-2 text-white"
                                                    icon={faEdit}
                                                    onClick={() => navigateToDetails(signage.ContentID)}
                                                />
                                            </div>

                                            {displayMedia(
                                                signage.ContentType,
                                                mediaUrls[signage.ContentID]
                                            )}
                                            <div>{signage.ContentName}</div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </div>
                        <div
                            className="signage-section"
                            id="scheduled-section"
                        >
                            <Card.Header
                                className="text-end"
                                id="coming-soon-gallery-header"
                            >{`Coming Soon (${scheduledSignages.length})`}</Card.Header>
                            <Card.Body>
                                <div className="row">
                                    {scheduledSignages.map((signage) => (
                                        <div
                                            className="col-lg-3 col-md-4 col-sm-6 col-12 text-center mx-2 m-2 py-2 border rounded-1"
                                            key={signage.ContentID}
                                        >
                                            <div className="scheduled-tag d-flex justify-content-between align-items-center w-100">
                                                Starting {moment(signage.StartDate).format('MM/DD/YYYY')}
                                                <FontAwesomeIcon
                                                    className="ms-2 text-white"
                                                    icon={faEdit}
                                                    onClick={() => navigateToDetails(signage.ContentID)}
                                                />
                                            </div>

                                            {displayMedia(
                                                signage.ContentType,
                                                mediaUrls[signage.ContentID]
                                            )}
                                            <div>{signage.ContentName}</div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </div>
                        {expiredSignages.length > 0 && (
                            <div
                                className="signage-section"
                                id="expired-section"
                            >
                                <Card.Header
                                    className="text-end"
                                    id="expired-gallery-header"
                                >
                                    {`Expired (${expiredSignages.length})`}
                                    {/*<FontAwesomeIcon*/}
                                    {/*  icon={faTrash}*/}
                                    {/*  onClick={handleOpenModal}*/}
                                    {/*  style={{ cursor: 'pointer', marginLeft: '10px' }}*/}
                                    {/*/>*/}
                                </Card.Header>
                                <Card.Body>
                                    <div className="row">
                                        {expiredSignages.map((signage) => (
                                            <div
                                                className="col-lg-3 col-md-4 col-sm-6 col-12 text-center mx-2 m-2 py-2 border rounded-1"
                                                key={signage.ContentID}
                                            >
                                                <div className="expired-tag d-flex justify-content-between align-items-center w-100">
                                                    Expired {moment(signage.EndDate).format('MM/DD/YYYY')}
                                                    <FontAwesomeIcon
                                                        className="ms-2 text-white"
                                                        icon={faEdit}
                                                        onClick={() => navigateToDetails(signage.ContentID)}
                                                    />
                                                </div>

                                                {displayMedia(
                                                    signage.ContentType,
                                                    mediaUrls[signage.ContentID]
                                                )}
                                                <div>{signage.ContentName}</div>
                                            </div>
                                        ))}
                                    </div>
                                </Card.Body>
                            </div>
                        )}
                    </Card>
                    <Modal
                        onHide={handleCloseModal}
                        show={showModal}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Confirm Deletion</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            Are you sure you want to delete all expired signage?
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onClick={handleCloseModal}
                                variant="secondary"
                            >
                                Cancel
                            </Button>
                            <Button variant="primary">Confirm</Button>
                        </Modal.Footer>
                    </Modal>
                </div>
            ) : (
                <div className="mt-5 p-2">
                    <div
                        className="alert alert-secondary d-flex justify-content-between align-items-center"
                        role="alert"
                    >
                        <FontAwesomeIcon
                            className="text-secondary"
                            icon={faCircleInfo}
                        />
                        <div className="ms-2 entertainment__action-info">
                            You have no Digital Signage. Tap the plus icon below to get
                            started.
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default DigitalSignage;
